import { Component, Inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../../../shared/models/option.model';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Model184Service } from '../../model-184.service';
import { SINO_AEAT } from '../../tax-catalogs';
import {
  CLAVE_RENTA_184,
  GASTOS_ACTIVIDAD_184,
  GASTOS_INMUEBLE_184,
  Model184Renta,
  Model184Validation,
  REGIMEN_184,
  SITUACION_INMUEBLE_184,
  SUBCLAVES_184,
  TIPO_ACTIVIDAD_184,
  normalizarRenta184,
  nuevaRenta184
} from '../taxes-184.model';

export interface Model184RentaDialogData {
  renta: Model184Renta | null;
  ejercicio: number;
  validaciones: Model184Validation[];
}

export interface Model184RentaDialogResult {
  renta: Model184Renta;
}

const NUMERICOS = ['valorAumentos', 'valorDisminuciones', 'ingresos', 'gastos', 'renta', 'porcentajeReduccion',
  'rentaReduccion', 'gananciasPerdidas', 'baseDeduccion', 'retenciones', 'diasArrendamiento'];

@Component({
  selector: 'app-model-184-renta-dialog',
  templateUrl: './model-184-renta-dialog.component.html',
  styleUrls: ['./model-184-renta-dialog.component.css'],
  standalone: false
})
export class Model184RentaDialogComponent {

  f: any;
  saving = false;
  vista: 'renta' | 'gastos' = 'renta';
  validaciones: Model184Validation[];
  fechaControl = new UntypedFormControl(null);

  readonly claveOptions: Option[] = CLAVE_RENTA_184;
  readonly regimenOptions: Option[] = REGIMEN_184;
  readonly tipoActividadOptions: Option[] = TIPO_ACTIVIDAD_184;
  readonly situacionOptions: Option[] = SITUACION_INMUEBLE_184;
  readonly sinoOptions: Option[] = SINO_AEAT;
  readonly gastosActividad = GASTOS_ACTIVIDAD_184;
  readonly gastosInmueble = GASTOS_INMUEBLE_184;

  static open(dialog: MatDialog, data: Model184RentaDialogData): Observable<Model184RentaDialogResult | null> {
    return dialog.open<Model184RentaDialogComponent, Model184RentaDialogData, Model184RentaDialogResult>(
      Model184RentaDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data
    }).afterClosed().pipe(map(resultado => resultado || null));
  }

  constructor(
    private dialogRef: MatDialogRef<Model184RentaDialogComponent, Model184RentaDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: Model184RentaDialogData,
    private service: Model184Service,
    private notification: NotificationService
  ) {
    const origen = data.renta ? normalizarRenta184(data.renta) : nuevaRenta184();

    this.f = {
      ...origen,
      criterioCobrosSN: origen.criterioCobros ? 'S' : 'N',
      gastosActividad: { ...origen.gastosActividad },
      gastosInmueble: { ...origen.gastosInmueble }
    };

    this.fechaControl.setValue(this.aFecha(origen.fechaAdquisicion));
    this.validaciones = data.validaciones || [];
  }

  get titulo(): string {
    return this.data.renta ? 'Editar renta de la entidad' : 'Nueva renta de la entidad';
  }

  get subtitulo(): string {
    return `Modelo 184 · ejercicio ${this.data.ejercicio}`;
  }

  get subclaveOptions(): Option[] {
    return SUBCLAVES_184[this.f.clave] || [];
  }

  get subclaveConfig(): any {
    return { label: 'Subclave', placeholder: 'Seleccionar', options: this.subclaveOptions };
  }

  mensaje(campo: string): Model184Validation | null {
    return this.validaciones.find(item => item.key === campo) || null;
  }

  get sueltas(): Model184Validation[] {
    return this.validaciones.filter(item => !item.key);
  }

  set(campo: string, valor: any) {
    this.f[campo] = valor;
    this.validaciones = this.validaciones.filter(item => item.key !== campo);
  }

  cambiarClave(clave: string) {
    this.set('clave', clave);

    const opciones = SUBCLAVES_184[clave] || [];

    this.set('subclave', opciones.length ? opciones[0].value : '');
  }

  trackByIndex(i: number) { return i; }

  private aFecha(texto: string): Date | null {
    if (!texto || texto.length !== 8) return null;

    const fecha = new Date(Number(texto.substring(0, 4)), Number(texto.substring(4, 6)) - 1, Number(texto.substring(6, 8)));

    return isNaN(fecha.getTime()) ? null : fecha;
  }

  private aTexto(fecha: any): string {
    if (!fecha) return '';

    const valor = new Date(fecha);

    if (isNaN(valor.getTime())) return '';

    return valor.getFullYear().toString().padStart(4, '0') +
      (valor.getMonth() + 1).toString().padStart(2, '0') +
      valor.getDate().toString().padStart(2, '0');
  }

  private numero(valor: any): number {
    const convertido = Number(String(valor ?? '').replace(',', '.'));

    return isNaN(convertido) ? 0 : convertido;
  }

  private aDto(): Model184Renta {
    const f = this.f;
    const dto: any = { ...f };

    NUMERICOS.forEach(campo => dto[campo] = this.numero(f[campo]));

    dto.criterioCobros = f.criterioCobrosSN === 'S';
    dto.fechaAdquisicion = this.aTexto(this.fechaControl.value);

    const actividad: { [concepto: string]: number } = {};
    const inmueble: { [concepto: string]: number } = {};

    this.gastosActividad.forEach(item => actividad[item.clave] = this.numero(f.gastosActividad[item.clave]));
    this.gastosInmueble.forEach(item => inmueble[item.clave] = this.numero(f.gastosInmueble[item.clave]));

    dto.gastosActividad = actividad;
    dto.gastosInmueble = inmueble;

    return normalizarRenta184(dto);
  }

  guardar() {
    if (this.saving) return;

    const renta = this.aDto();

    renta.origen = 'MANUAL';

    this.saving = true;
    this.dialogRef.disableClose = true;

    this.service.saveRent(this.data.ejercicio, renta)
      .then(guardada => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.dialogRef.close({ renta: guardada });
      })
      .catch(err => {
        this.saving = false;
        this.dialogRef.disableClose = false;

        const mensajes: string[] = err?.error?.error?.messages;

        this.notification.error(mensajes?.length ? mensajes.join('. ') : 'No se ha podido guardar la renta');
      });
  }

  cerrar() {
    if (this.saving) return;

    this.dialogRef.close(undefined);
  }
}
